import { dateFilterAfter } from '@/utils/date';
import { ListResult } from 'pocketbase';
import {
  ActivityChecklistResponse,
  DailySummaryCategoryDefinitionResponse,
  DailySummaryCategorySentimentDefinitionResponse,
  DailySummaryCategorySentimentResponse,
  DailySummaryResponse,
  DiaperResponse,
  DiaperStatusRecord,
  DiaperStatusResponse,
  FeedingResponse,
  KidActivitiesResponse,
  KidActivityTypesResponse,
  KidsRecord,
  KidsResponse,
  MomentsDailyEntryResponse,
  MomentsUploadsResponse,
  ParentMoodsResponse,
  SleepResponse,
  TypedPocketBase,
  UsersRecord,
  UsersResponse,
} from '../../types/pocketbase-types';

export class Store {
  protected readonly pb: TypedPocketBase;

  constructor(pb: TypedPocketBase) {
    this.pb = pb;
  }

  public async getKidBySlug(slug: KidsResponse<KidsRecord>['slug']): Promise<KidItem | null> {
    try {
      return await this.pb.collection('kids').getFirstListItem('slug', {
        filter: this.pb.filter('slug = {:slug}', { slug: slug.toString() }),
        requestKey: null,
      });
    } catch (e) {
      if (e instanceof Error && 'status' in e && e.status === 404) {
        return null;
      }
      throw e;
    }
  }

  public async getActivityChecklist({
    slug,
    page,
    perPage,
  }: {
    slug: string;
    page: number;
    perPage: number;
  }): Promise<ListResult<ChecklistItem>> {
    return await this.pb.collection('activity_checklist').getList(page, perPage, {
      filter: this.pb.filter('kid.slug = {:slug} && completed_at = null', {
        slug: slug,
      }),
      sort: '-added',
      expand: 'creator,diaper,feeding,sleep,diaper.status',
    });
  }

  public async parentsOfKid(slug: string): Promise<ParentItem[]> {
    return this.pb.collection('users').getFullList({
      filter: this.pb.filter('parent_of.slug ?= {:slug}', { slug }),
      requestKey: null,
    });
  }

  public async allMoodsOfParents(
    slug: string,
    parents: Pick<UsersResponse<UsersRecord>, 'id'>[],
    daysAgo?: number,
  ): Promise<MoodItem[]> {
    const date = daysAgo
      ? this.pb.filter(`created > {:daysAgo}`, {
          daysAgo: dateFilterAfter(daysAgo),
        })
      : '';
    const orFilter = parents
      .map((p) => this.pb.filter(`parent = {:parent}`, { parent: p.id }))
      .join(' || ');

    const kidFilter = this.pb.filter(`kid.slug = {:slug}`, { slug: slug });
    const filters = [kidFilter, `(${orFilter})`];
    if (date) {
      filters.push(date);
    }
    const filter = filters.join(' && ');

    return this.pb.collection('parent_moods').getFullList<MoodItem>({
      filter,
      sort: '-created',
      expand: 'parent',
      requestKey: null,
    });
  }

  public async mostRecentMoodOfParents(
    slug: string,
    parents: Pick<UsersResponse<UsersRecord>, 'id'>[],
  ): Promise<MoodItem[]> {
    const parentMoods: (MoodItem | null)[] = (
      await Promise.all(
        parents.map((parent) => {
          return this.pb.collection('parent_moods').getList<MoodItem>(1, 1, {
            filter: this.pb.filter('parent = {:parent} && kid.slug = {:slug}', {
              parent: parent.id,
              slug: slug,
            }),
            sort: '-created',
            requestKey: null,
          });
        }),
      )
    ).map((moods) => (moods.items.length ? moods.items[0] : null));
    return parentMoods.filter((m) => m !== null) as MoodItem[];
  }

  async fetchDiapersWithinDays(slug: string, daysAgo?: number): Promise<DiaperItem[]> {
    return await this.pb
      .collection('diaper')
      .getFullList<DiaperResponse<{ status: DiaperStatusRecord }>>({
        filter:
          this.pb.filter('kid.slug = {:slug}', { slug: slug }) +
          (daysAgo
            ? this.pb.filter(`&& date > {:daysAgo}`, {
                daysAgo: dateFilterAfter(daysAgo),
              })
            : ''),
        expand: 'status',
        requestKey: null,
      });
  }

  async fetchDiapersWithinDaysSlug(kidslug: string, daysAgo?: number): Promise<DiaperItem[]> {
    return await this.pb
      .collection('diaper')
      .getFullList<DiaperResponse<{ status: DiaperStatusRecord }>>({
        filter:
          this.pb.filter('kid.slug = {:kidslug}', { kidslug: kidslug }) +
          (daysAgo
            ? this.pb.filter(`&& date > {:daysAgo}`, {
                daysAgo: dateFilterAfter(daysAgo),
              })
            : ''),
        expand: 'status',
        requestKey: null,
      });
  }

  async fetchFeedingsWithinDays(slug: string, daysAgo?: number): Promise<FeedingItem[]> {
    return await this.pb.collection('feeding').getFullList<FeedingItem>({
      filter:
        this.pb.filter('kid.slug = {:slug}', { slug: slug }) +
        (daysAgo
          ? this.pb.filter(`&& date > {:daysAgo}`, {
              daysAgo: dateFilterAfter(daysAgo),
            })
          : ''),
      requestKey: null,
    });
  }

  async fetchSleepsWithinDays(slug: string, daysAgo?: number): Promise<SleepItem[]> {
    return await this.pb.collection('sleep').getFullList<SleepItem>({
      filter:
        this.pb.filter('kid.slug = {:slug}', { slug: slug }) +
        (daysAgo
          ? this.pb.filter(` && start > {:daysAgo}`, {
              daysAgo: dateFilterAfter(daysAgo),
            })
          : ''),
      requestKey: null,
    });
  }

  fetchChartDataInRange({
    slug,
    fromDate,
    toDate,
  }: {
    slug: string;
    fromDate?: Date;
    toDate?: Date;
  }): Promise<{
    diapers: DiaperItem[];
    feedings: FeedingItem[];
    sleeps: SleepItem[];
  }> {
    if (!slug && !fromDate && !toDate) {
      throw new Error('Missing required parameters');
    }
    return Promise.all([
      this.fetchDiapersInRange(slug, fromDate, toDate),
      this.fetchFeedingsInRange(slug, fromDate, toDate),
      this.fetchSleepsInRange(slug, fromDate, toDate),
    ]).then(([diapers, feedings, sleeps]) => {
      return { diapers, feedings, sleeps };
    });
  }

  async fetchDiapersInRange(slug: string, fromDate?: Date, toDate?: Date): Promise<DiaperItem[]> {
    const filters = [this.pb.filter('kid.slug = {:slug}', { slug: slug })];
    if (fromDate) {
      filters.push(
        this.pb.filter(`date >= {:from}`, {
          from: dateToPBDate(fromDate),
        }),
      );
    }
    if (toDate) {
      filters.push(
        this.pb.filter(`date < {:to}`, {
          to: dateToPBDate(toDate),
        }),
      );
    }
    return await this.pb
      .collection('diaper')
      .getFullList<DiaperResponse<{ status: DiaperStatusRecord }>>({
        filter: filters.join(' && '),
        expand: 'status',
        requestKey: null,
      });
  }

  async fetchFeedingsInRange(slug: string, fromDate?: Date, toDate?: Date): Promise<FeedingItem[]> {
    const filters = [this.pb.filter('kid.slug = {:slug}', { slug: slug })];
    if (fromDate) {
      filters.push(
        this.pb.filter(`date >= {:from}`, {
          from: dateToPBDate(fromDate),
        }),
      );
    }
    if (toDate) {
      filters.push(
        this.pb.filter(`date < {:to}`, {
          to: dateToPBDate(toDate),
        }),
      );
    }
    return await this.pb.collection('feeding').getFullList<FeedingItem>({
      filter: filters.join(' && '),
      requestKey: null,
    });
  }

  async fetchSleepsInRange(slug: string, fromDate?: Date, toDate?: Date): Promise<SleepItem[]> {
    const filters = [this.pb.filter('kid.slug = {:slug}', { slug: slug })];
    if (fromDate) {
      filters.push(
        this.pb.filter(`start >= {:from}`, {
          from: dateToPBDate(fromDate),
        }),
      );
    }
    if (toDate) {
      filters.push(
        this.pb.filter(`start < {:to}`, {
          to: dateToPBDate(toDate),
        }),
      );
    }
    return await this.pb.collection('sleep').getFullList<SleepItem>({
      filter: filters.join(' && '),
      requestKey: null,
    });
  }

  async allKids(): Promise<KidItem[]> {
    return this.pb.collection('kids').getFullList();
  }

  async getExistingOpenSleep(slug: string): Promise<SleepItem | null> {
    const existingOpenSleep = await this.pb.collection('sleep').getList(0, 1, {
      filter: this.pb.filter('kid.slug = {:slug} && end = {:end}', {
        slug,
        end: null,
      }),
    });
    return existingOpenSleep.items.length ? existingOpenSleep.items[0] : null;
  }

  async getSleepPage({
    slug,
    page,
    perPage,
  }: {
    slug: string;
    page: number;
    perPage: number;
  }): Promise<ListResult<SleepItem>> {
    return await this.pb.collection('sleep').getList(page, perPage, {
      filter: this.pb.filter('kid.slug = {:slug}', { slug }),
      sort: '-start',
    });
  }

  async getFeedingsPage({
    slug,
    page,
    perPage,
  }: {
    slug: string;
    page: number;
    perPage: number;
  }): Promise<ListResult<FeedingItem>> {
    return await this.pb.collection('feeding').getList(page, perPage, {
      filter: this.pb.filter('kid.slug = {:slug}', { slug }),
      sort: '-date',
    });
  }

  async getDiapersPage({
    slug,
    page,
    perPage,
  }: {
    slug: string;
    page: number;
    perPage: number;
  }): Promise<ListResult<DiaperItem>> {
    return await this.pb.collection('diaper').getList(page, perPage, {
      filter: this.pb.filter('kid.slug = {:slug}', { slug }),
      expand: 'status',
      sort: '-date',
    });
  }

  getAllDiaperStatus(): Promise<DiaperStatusResponse<DiaperStatusRecord>[]> {
    return this.pb.collection('diaper_status').getFullList({
      sort: 'order',
    });
  }

  async getAllChecklistItems(
    slug: string,
    daysAgo?: number,
    includeAll?: boolean,
  ): Promise<ActivityChecklistItem[]> {
    let filter = this.pb.filter('kid.slug = {:slug}', {
      slug,
    });
    if (!includeAll) {
      filter += ' && completed_at = null';
    }
    if (daysAgo) {
      filter += this.pb.filter(` && added > {:daysAgo}`, {
        daysAgo: dateFilterAfter(daysAgo),
      });
    }

    const checklist = await this.pb
      .collection('activity_checklist')
      .getFullList<ActivityChecklistItem>({
        filter,
        sort: '-added',
        expand: 'creator,diaper,feeding,sleep,diaper.status',
        requestKey: null,
      });
    return checklist;
  }

  getAllChecklistItemsArchived(slug: string): Promise<ActivityChecklistItem[]> {
    return this.pb.collection('activity_checklist').getFullList<ActivityChecklistItem>({
      filter: this.pb.filter('kid.slug = {:slug} && completed_at != null', {
        slug,
      }),
      sort: '-completed_at',
      expand: 'creator,diaper,feeding,sleep,diaper.status',
      requestKey: null,
    });
  }

  async getSleepById(sleepId: string): Promise<SleepItem | null> {
    try {
      return await this.pb.collection('sleep').getOne(sleepId, { requestKey: null });
    } catch (e) {
      if (e instanceof Error && 'status' in e && e.status === 404) {
        return null;
      }
      throw e;
    }
  }

  async getFeedingById(feedingId: string): Promise<FeedingItem | null> {
    try {
      return await this.pb.collection('feeding').getOne(feedingId, { requestKey: null });
    } catch (e) {
      if (e instanceof Error && 'status' in e && e.status === 404) {
        return null;
      }
      throw e;
    }
  }

  async getDiaperById(diaperId: string): Promise<DiaperItem | null> {
    try {
      return await this.pb
        .collection('diaper')
        .getOne(diaperId, { requestKey: null, expand: 'status' });
    } catch (e) {
      if (e instanceof Error && 'status' in e && e.status === 404) {
        return null;
      }
      throw e;
    }
  }

  async createSleep(input: CreateSleepRequest): Promise<SleepItem> {
    return await this.pb.collection('sleep').create({
      start: input.start.toISOString(),
      end: input.end?.toISOString() ?? null,
      kid: input.kid,
      notes: input.notes,
    });
  }

  async updateSleep(input: UpdateSleepRequest): Promise<SleepItem> {
    return this.pb.collection('sleep').update(input.id, {
      start: input.start,
      end: input.end,
      notes: input.notes,
    });
  }

  createChecklistItem(
    input: {
      addToChecklist: boolean;
      kidId: string;
      activity: { feeding: string } | { diaper: string } | { sleep: string };
    },
    creator: ParentItem['id'],
  ) {
    return this.pb.collection('activity_checklist').create({
      ...input.activity,
      kid: input.kidId,
      status: 'open',
      creator: creator,
      added: new Date().toISOString(),
      notes: null,
    });
  }

  deleteItem(id: string, collection: string) {
    return this.pb.collection(collection).delete(id);
  }

  async createDiaper(input: CreateDiaperRequest): Promise<SleepItem> {
    return await this.pb.collection('diaper').create({
      date: input.date.toISOString(),
      kid: input.kid,
      notes: input.notes,
      status: input.diaperStatusId,
    });
  }

  async updateDiaper(input: UpdateDiaperRequest): Promise<SleepItem> {
    return this.pb.collection('diaper').update(input.id, {
      date: input.date,
      notes: input.notes,
      status: input.diaperStatusId,
    });
  }

  async updateActivity(input: UpdateActivityRequest): Promise<KidActivityItem> {
    return this.pb.collection('kid_activities').update(input.id, {
      date: input.date,
      notes: input.notes,
      kid_activity_type: input.activityTypeId,
    });
  }

  async createFeeding(input: CreateFeedingRequest): Promise<SleepItem> {
    return await this.pb.collection('feeding').create({
      date: input.date.toISOString(),
      kid: input.kid,
      notes: input.notes,
      kind: input.kind,
      leftBreastUsed: input.leftBreastUsed,
      rightBreastUsed: input.rightBreastUsed,
      amount: input.amount,
      amountUnit: input.amountUnit,
    });
  }

  async createActivity(input: CreateActivityRequest): Promise<KidActivityItem> {
    return await this.pb.collection('kid_activities').create({
      date: input.date.toISOString(),
      kid: input.kid,
      notes: input.notes,
      kid_activity_type: input.activityTypeId,
    });
  }

  async createActivityType(input: CreateActivityTypeRequest): Promise<KidActivityTypesItem> {
    return await this.pb.collection('kid_activity_types').create({
      kid: input.kid,
      name: input.name,
    });
  }

  async updateActivityType(input: UpdateActivityTypeRequest): Promise<KidActivityTypesItem> {
    return await this.pb.collection('kid_activity_types').update(input.id, {
      name: input.name,
      hidden: input.hidden,
    });
  }

  async updateFeeding(input: UpdateFeedingRequest): Promise<SleepItem> {
    return this.pb.collection('feeding').update(input.id, {
      date: input.date.toISOString(),
      notes: input.notes,
      kind: input.kind,
      leftBreastUsed: input.leftBreastUsed,
      rightBreastUsed: input.rightBreastUsed,
      amount: input.amount,
      amountUnit: input.amountUnit,
    });
  }

  updateActivityChecklist({
    activityId,
    status,
    completedAt,
    completedBy,
  }: {
    activityId: string;
    status: 'completed' | 'open';
    completedAt: Date | null;
    completedBy: UserItem['id'] | null;
  }) {
    return this.pb.collection('activity_checklist').update(activityId, {
      status,
      completed_at: completedAt?.toISOString() ?? null,
      completed_by: completedBy,
    });
  }

  createParentMood(input: { mood: string; parentId: UserItem['id']; kidId: KidItem['id'] }) {
    return this.pb.collection('parent_moods').create({
      kid: input.kidId,
      mood: input.mood,
      parent: input.parentId,
    });
  }

  async saveMoment(input: { date: Date; text: string; kidId: string }) {
    const existing = await this.pb.collection('moments_daily_entry').getList(1, 1, {
      filter: this.pb.filter('kid = {:kid} && date = {:date}', {
        kid: input.kidId,
        date: dateToPBDate(input.date),
      }),
    });
    if (existing.items.length) {
      return this.pb.collection('moments_daily_entry').update(existing.items[0].id, {
        notes: input.text,
      });
    }

    return this.pb.collection('moments_daily_entry').create({
      kid: input.kidId,
      date: input.date.toISOString(),
      notes: input.text,
    });
  }

  public async getMoments(kidId: string): Promise<MomentItem[]> {
    return await this.pb.collection('moments_daily_entry').getFullList({
      filter: this.pb.filter('kid = {:kidId}', { kidId: kidId.toString() }),
      expand: 'moments_uploads_via_moment,moments_uploads_via_moment.uploader',
      sort: '-date',
      requestKey: null,
    });
  }

  public async getMomentsDaysAgo(slug: string, daysAgo?: number): Promise<MomentItem[]> {
    let filter = this.pb.filter('kid.slug = {:slug}', { slug: slug });
    const date = daysAgo
      ? this.pb.filter(`date >= {:daysAgo}`, {
          daysAgo: dateFilterAfter(daysAgo),
        })
      : '';

    filter = date ? `(${filter}) && ${date}` : filter;

    return await this.pb.collection('moments_daily_entry').getFullList({
      filter,
      sort: '-date',
      expand: 'moments_uploads_via_moment,moments_uploads_via_moment.uploader',
      requestKey: null,
    });
  }

  getKidActivitiesDaysAgo(slug: string, daysAgo?: number): Promise<KidActivityItem[]> {
    let filter = this.pb.filter('kid.slug = {:slug}', {
      slug: slug,
    });
    const date = daysAgo
      ? this.pb.filter(`date >= {:daysAgo}`, {
          daysAgo: dateFilterAfter(daysAgo),
        })
      : '';

    filter = date ? `(${filter}) && ${date}` : filter;

    return this.pb.collection('kid_activities').getFullList({
      filter,
      sort: '-date',
      expand: 'kid_activity_type',
      requestKey: null,
    });
  }

  getKidActivityTypes(slug: string, showAll?: boolean): Promise<KidActivityTypesItem[]> {
    let filter = this.pb.filter('kid.slug = {:slug}', {
      slug: slug,
    });
    if (!showAll) {
      filter += ' && hidden = false';
    }

    return this.pb.collection('kid_activity_types').getFullList({
      filter,
      sort: '-name',
      // expand: 'kid_activity_type',
      requestKey: null,
    });
  }

  async getActivitiesPage({
    slug,
    page,
    perPage,
  }: {
    slug: string;
    page: number;
    perPage: number;
  }): Promise<ListResult<KidActivityItem>> {
    return await this.pb.collection('kid_activities').getList(page, perPage, {
      filter: this.pb.filter('kid.slug = {:slug}', { slug }),
      expand: 'kid_activity_type',
      sort: '-date',
    });
  }

  async getCategorySentiments(): Promise<DailySummaryCategorySentimentDefinitionItem[]> {
    const res: DailySummaryCategorySentimentDefinitionResponse<{
      daily_summary_category: DailySummaryCategoryDefinitionItem;
    }>[] = await this.pb.collection('daily_summary_category_sentiment_definition').getFullList({
      // filter: this.pb.filter('kid.slug = {:slug}', { slug }),
      // sort: 'sentiment_definition.order',
      expand: 'daily_summary_category',
      requestKey: null,
    });
    return res.map(this.mapSentimentDefinition()).filter(notEmpty);
  }

  private mapSentimentDefinition() {
    return (
      r: DailySummaryCategorySentimentDefinitionResponse<{
        daily_summary_category: DailySummaryCategoryDefinitionItem;
      }>,
    ): DailySummaryCategorySentimentDefinitionItem | null => {
      const daily_summary_category_definition = r.expand?.daily_summary_category;
      if (!daily_summary_category_definition) {
        return null;
      }
      return {
        ...r,
        daily_summary_category_definition,
      };
    };
  }

  private mapSentiment() {
    return (r: DailySummarySentimentQueryResponse): DailySummarySentimentItem | null => {
      if (!r.expand?.sentiment_definition) {
        return null;
      }
      const sentiment_definition = this.mapSentimentDefinition()(r.expand.sentiment_definition);
      if (!sentiment_definition) {
        return null;
      }
      return {
        ...r,
        daily_summary_category_sentiment_definition: sentiment_definition,
      };
    };
  }

  async getDailySummary(kid: KidItem['id'], date: Date): Promise<DailySummaryItem | null> {
    const res: ListResult<
      DailySummaryResponse<{
        daily_summary_category_sentiment_via_daily_summary: DailySummarySentimentQueryResponse[];
      }>
    > = await this.pb.collection('daily_summary').getList(1, 1, {
      filter: this.pb.filter('kid = {:kid} && date = {:date}', {
        kid: kid,
        date: dateToPBDate(date),
      }),

      expand:
        'daily_summary_category_sentiment_via_daily_summary,daily_summary_category_sentiment_via_daily_summary.sentiment_definition,daily_summary_category_sentiment_via_daily_summary.sentiment_definition.daily_summary_category',
      requestKey: null,
    });
    if (res.items.length === 0) {
      return null;
    }
    const sentiments =
      res.items[0].expand?.daily_summary_category_sentiment_via_daily_summary
        ?.map(this.mapSentiment())
        .filter(notEmpty) || [];

    const sentimentsGroupedByCategory = sentiments.reduce(
      (acc, sentiment) => {
        const category =
          sentiment.daily_summary_category_sentiment_definition.daily_summary_category;
        acc[category] = (acc[category] || []).concat(sentiment);
        return acc;
      },
      {} as Record<string, DailySummarySentimentItem[]>,
    );
    const out = {
      ...res.items[0],
      sentiments: sentiments,
      sentimentsGroupedByCategory,
    };

    return out;
  }

  async getDailySummariesDaysAgo(
    kid: KidItem['slug'],
    daysAgo?: number,
  ): Promise<DailySummarySentimentSummary[]> {
    const date = daysAgo
      ? this.pb.filter(`date > {:daysAgo}`, {
          daysAgo: dateFilterAfter(daysAgo),
        })
      : '';
    const filterMain = this.pb.filter('kid.slug = {:kid} ', {
      kid: kid,
    });
    const beforeNow = this.pb.filter('date < {:now}', {
      now: dateToPBDate(new Date()),
    });
    const filter = [filterMain, date, beforeNow].filter(notEmpty).join(' && ');

    const res: DailySummaryResponse<{
      daily_summary_category_sentiment_via_daily_summary: DailySummarySentimentQueryResponse[];
    }>[] = await this.pb.collection('daily_summary').getFullList({
      filter: filter,
      expand:
        'daily_summary_category_sentiment_via_daily_summary,daily_summary_category_sentiment_via_daily_summary.sentiment_definition,daily_summary_category_sentiment_via_daily_summary.sentiment_definition.daily_summary_category',
      requestKey: null,
    });
    const allSentiments = res.map((r) => {
      const sentiments =
        r.expand?.daily_summary_category_sentiment_via_daily_summary
          ?.map(this.mapSentiment())
          .filter(notEmpty) || [];

      const sentimentsGroupedByCategory = sentiments.reduce(
        (acc, sentiment) => {
          const category =
            sentiment.daily_summary_category_sentiment_definition.daily_summary_category;
          acc[category] = (acc[category] || []).concat(sentiment);
          return acc;
        },
        {} as Record<string, DailySummarySentimentItem[]>,
      );
      return { id: r.id, date: r.date, sentiments, sentimentsGroupedByCategory };
    });

    return allSentiments;
  }

  //in reality this would be called one by one as sentiment is marked
  async createDailySummary(input: {
    date: Date;
    kid: KidItem['id'];
    notes: string;
    parent: ParentItem['id'];
    sentiments: DailySummaryCategorySentimentDefinitionItem[];
  }): Promise<DailySummaryItem> {
    const dsbefore = await this.getDailySummary(input.kid, input.date);
    let ds: DailySummaryResponse;
    if (dsbefore) {
      ds = dsbefore;
    } else {
      try {
        ds = await this.pb.collection('daily_summary').create({
          date: input.date.toISOString(),
          kid: input.kid,
          notes: input.notes,
          created_by_parent: input.parent,
        });
      } catch (e) {
        throw e;
        // if (e instanceof ClientResponseError) {
        //   // e.message?.includes('validation_not_unique')
        //   const found = Object.entries(e.response.data).some(([key, value]) => {
        //     return value?.code === 'validation_not_unique';
        //   });
        //   if (!found) {
        //     throw e;
        //   }
        //   ds = await this.getDailySummary(input.kid, input.date);
        // } else {
        //   throw e;
        // }
      }
    }
    const sentiments: DailySummarySentimentItem[] = [];
    for (const sentiment of input.sentiments) {
      //now we need to update this
      const mapped = await this.createDailySummarySentiment({
        dailySummaryId: ds.id,
        sentimentDefinitionId: sentiment.id,
      });
      if (mapped) {
        sentiments.push(mapped);
      }
    }
    return {
      ...ds,
      sentiments,
    };
  }

  async createDailySummarySentiment(input: {
    dailySummaryId: DailySummaryItem['id'];
    sentimentDefinitionId: DailySummaryCategorySentimentDefinitionItem['id'];
  }): Promise<DailySummarySentimentItem | null> {
    const sentimentOut: DailySummarySentimentQueryResponse = await this.pb
      .collection('daily_summary_category_sentiment')
      .create(
        {
          daily_summary: input.dailySummaryId,
          sentiment_definition: input.sentimentDefinitionId,
        },
        {
          requestKey: null,
          expand: 'sentiment_definition,sentiment_definition.daily_summary_category',
        },
      );
    return this.mapSentiment()(sentimentOut);
  }

  async getOrCreateEmptyDailySummary(input: {
    date: Date;
    kid: KidItem['id'];
    notes: string;
    parent: ParentItem['id'];
  }): Promise<DailySummaryItem> {
    const dsbefore = await this.getDailySummary(input.kid, input.date);
    if (dsbefore) {
      return dsbefore;
    } else {
      return this.pb.collection('daily_summary').create({
        date: input.date.toISOString(),
        kid: input.kid,
        notes: input.notes,
        created_by_parent: input.parent,
      });
    }
  }
  async getDailySummarySentiment(input: {
    dailySummaryId: DailySummaryItem['id'];
    sentimentDefinitionId: DailySummaryCategorySentimentDefinitionItem['id'];
  }): Promise<DailySummarySentimentItem | null> {
    const result: ListResult<DailySummarySentimentQueryResponse> = await this.pb
      .collection('daily_summary_category_sentiment')
      .getList(1, 1, {
        filter: this.pb.filter(
          'daily_summary = {:dailySummaryId} && sentiment_definition = {:sentimentDefinitionId}',
          {
            dailySummaryId: input.dailySummaryId,
            sentimentDefinitionId: input.sentimentDefinitionId,
          },
        ),
        requestKey: null,
        expand: 'sentiment_definition,sentiment_definition.daily_summary_category',
      });
    if (result.items.length === 0) {
      return null;
    }
    return this.mapSentiment()(result.items[0]);
  }
  async toggleSentiment(input: {
    dailySummaryId: DailySummaryItem['id'];
    sentimentDefinitionId: DailySummaryCategorySentimentDefinitionItem['id'];
  }) {
    const existing = await this.getDailySummarySentiment(input);
    if (!existing) {
      return this.createDailySummarySentiment(input);
    } else {
      await this.pb.collection('daily_summary_category_sentiment').delete(existing.id);
      return null;
    }
  }

  async updateDailySummaryNotes(input: {
    dailySummaryId: DailySummaryItem['id'];
    notes: string;
  }): Promise<DailySummaryItem> {
    return this.pb.collection('daily_summary').update(input.dailySummaryId, {
      notes: input.notes,
    });
  }
}
const notEmpty = <T>(r: T | null | undefined): r is T => r !== null && r !== undefined;

type CreateSleepRequest = {
  start: Date;
  end: Date | null;
  kid: KidItem['id'];
  notes: string | null;
};
type UpdateSleepRequest = Omit<CreateSleepRequest, 'kid'> & {
  id: SleepItem['id'];
};
type CreateDiaperRequest = {
  date: Date;
  kid: KidItem['id'];
  notes: string | null;
  diaperStatusId: DiaperStatusResponse<DiaperStatusRecord>['id'];
};
type UpdateDiaperRequest = Omit<CreateDiaperRequest, 'kid'> & {
  id: DiaperItem['id'];
};
type CreateActivityRequest = {
  date: Date;
  kid: KidItem['id'];
  notes: string | null;
  activityTypeId: KidActivityTypesItem['id'];
};
type UpdateActivityRequest = Omit<CreateActivityRequest, 'kid'> & {
  id: KidActivityItem['id'];
};

type CreateActivityTypeRequest = {
  kid: KidItem['id'];
  name: KidActivityTypesItem['name'];
};
type UpdateActivityTypeRequest = Omit<CreateActivityTypeRequest, 'kid'> & {
  id: KidActivityTypesItem['id'];
  hidden: boolean;
};

type FeedingKind = 'nursing' | 'expressing' | 'formula';
type CreateFeedingRequest = {
  date: Date;
  kid: KidItem['id'];
  notes: string | null;
  kind: FeedingKind;
  leftBreastUsed: boolean | null;
  rightBreastUsed: boolean | null;
  amount: number | null;
  amountUnit: 'oz';
};
type UpdateFeedingRequest = Omit<CreateFeedingRequest, 'kid'> & {
  id: FeedingItem['id'];
};

export type DiaperItem = DiaperResponse<{ status: DiaperStatusRecord }>;
export type DiaperStatusItem = DiaperStatusResponse<DiaperStatusRecord>;
export type FeedingItem = FeedingResponse;
export type SleepItem = SleepResponse;
export type MoodItem = ParentMoodsResponse<{ parent: UsersResponse }>;
export type KidItem = KidsResponse<KidsRecord>;
export type ActivityChecklistItem = ActivityChecklistResponse<{
  creator: UsersResponse<UsersRecord>;
  diaper: DiaperResponse<{ status: DiaperStatusRecord }> | null;
  feeding: FeedingResponse | null;
  sleep: SleepResponse | null;
}>;
export type ParentItem = UsersResponse<UsersRecord>;
export type MomentUploadItemWithUser = MomentsUploadsResponse<{ uploader: UsersResponse }>;
export type MomentUploadItem = MomentUploadItemWithUser; //MomentsUploadsResponse<MomentsUploadsRecord>;
export type MomentItem = MomentsDailyEntryResponse<{
  moments_uploads_via_moment: MomentUploadItem[];
}>;
export type UserItem = UsersResponse<UsersRecord>;
export type ChecklistItem = ActivityChecklistResponse<{
  creator: UsersResponse<UsersRecord>;
  diaper: DiaperResponse<{ status: DiaperStatusRecord }> | null;
  feeding: FeedingResponse | null;
  sleep: SleepResponse | null;
}>;

export type KidActivityTypesItem = KidActivityTypesResponse;
export type KidActivityItem = KidActivitiesResponse<{
  kid_activity_type: KidActivityTypesItem;
}>;

export type DailySummaryCategoryDefinitionItem = DailySummaryCategoryDefinitionResponse;
export type DailySummaryCategorySentimentDefinitionItem =
  DailySummaryCategorySentimentDefinitionResponse & {
    daily_summary_category_definition: DailySummaryCategoryDefinitionItem;
  };
export type DailySummarySentimentItem = DailySummaryCategorySentimentResponse & {
  daily_summary_category_sentiment_definition: DailySummaryCategorySentimentDefinitionItem;
};
export type DailySummaryItem = DailySummaryResponse & { sentiments: DailySummarySentimentItem[] };

export type CreateDailySummarySentimentRequest = {
  sentiment_definition: DailySummaryCategorySentimentDefinitionItem;
};

export type DailySummarySentimentQueryResponse = DailySummaryCategorySentimentResponse<{
  sentiment_definition: DailySummaryCategorySentimentDefinitionResponse<{
    daily_summary_category: DailySummaryCategoryDefinitionItem;
  }>;
}>;

const dateToPBDate = (date: Date) => {
  return date.toISOString().replace('T', ' ');
};

export type DailySummarySentimentSummary = {
  id: string;
  date: string;
  sentiments: DailySummarySentimentItem[];
  sentimentsGroupedByCategory: Record<string, DailySummarySentimentItem[]>;
};
